import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import type { DragEndEvent } from "@dnd-kit/dom";
import {
  DragDropProvider,
  KeyboardSensor,
  PointerSensor,
} from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { sortUhCats } from "../services";
import { UhCatOption } from "../types";

const SortableRow = ({
  uc,
  index,
  total,
  isSaving,
  onMove,
}: {
  uc: UhCatOption;
  index: number;
  total: number;
  isSaving: boolean;
  onMove: (index: number, direction: -1 | 1) => void;
}) => {
  const { ref, handleRef, isDragging } = useSortable({
    id: uc.id,
    index,
  });

  return (
    <ListItem
      ref={ref}
      disableGutters
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        opacity: isDragging ? 0.3 : 1,
      }}
    >
      <IconButton
        ref={handleRef}
        size="small"
        disableRipple
        aria-label="Déplacer la catégorie"
        sx={{ cursor: "grab", color: "text.disabled", p: 0.5 }}
        disabled={isSaving}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ width: 24, color: "text.secondary" }}>
        {index + 1}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {uc.shortName}
      </Typography>
      <Box>
        <IconButton
          size="small"
          aria-label="Monter la catégorie"
          onClick={() => onMove(index, -1)}
          disabled={index === 0 || isSaving}
        >
          <KeyboardArrowUpIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Descendre la catégorie"
          onClick={() => onMove(index, 1)}
          disabled={index === total - 1 || isSaving}
        >
          <KeyboardArrowDownIcon fontSize="small" />
        </IconButton>
      </Box>
    </ListItem>
  );
};

interface Props {
  uhCats: UhCatOption[];
  onReordered: (uhCats: UhCatOption[]) => void;
  accommodationId: number;
}

export default function UhCatRanking({
  uhCats,
  onReordered,
  accommodationId,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  const doSave = (reordered: UhCatOption[]) => {
    const updated = reordered.map((u, i) => ({ ...u, order: i + 1 }));
    const previous = uhCats;
    onReordered(updated);
    setIsSaving(true);
    sortUhCats(
      updated.map((u) => u.id),
      accommodationId
    )
      .catch((e: any) => {
        enqueueSnackbar(e?.message ?? "Erreur lors de l'enregistrement", {
          variant: "error",
        });
        onReordered(previous);
      })
      .finally(() => setIsSaving(false));
  };

  const move = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= uhCats.length) return;
    const reordered = [...uhCats];
    const tmp = reordered[index];
    reordered[index] = reordered[newIndex]!;
    reordered[newIndex] = tmp!;
    doSave(reordered);
  };

  const handleDragEnd = (event: Parameters<DragEndEvent>[0]) => {
    if (isSaving) return;
    const { operation } = event;
    if (!isSortableOperation(operation) || !operation.source) return;
    const fromIndex = operation.source.initialIndex;
    const toIndex = operation.source.index;
    if (fromIndex === toIndex) return;
    const reordered = [...uhCats];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, removed!);
    doSave(reordered);
  };

  return (
    <Box>
      {isSaving && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Enregistrement...</Typography>
        </Box>
      )}

      <DragDropProvider
        sensors={[PointerSensor, KeyboardSensor]}
        onDragEnd={handleDragEnd}
      >
        <List dense disablePadding>
          {uhCats.map((uc, index) => (
            <SortableRow
              key={uc.id}
              uc={uc}
              index={index}
              total={uhCats.length}
              isSaving={isSaving}
              onMove={move}
            />
          ))}
        </List>
      </DragDropProvider>
    </Box>
  );
}
